import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField
} from '@mui/material';
import { ContentContainer, ScrollableSection, DownloadButton, MapPreviewContainer } from './HistorialModalStyle';
import { Download as DownloadIcon, Delete as DeleteIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ReactMap, { Source, Layer, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { API_BASE_PYTHON_SERVICE, API_BASE_URL } from '../../utils/config';

const HistorialContent = ({
                              analyses,
                              loading,
                              onClose,
                              selectedFiles = [],
                              onCheckboxChange = () => {}
                          }) => {
    const { userData } = useAuth();

    // =========== ESTADOS PARA LAS DIFERENTES CAPAS (geojson) ==============
    const [currentGeojson, setCurrentGeojson] = useState(null);
    const [currentNoFiltradas, setCurrentNoFiltradas] = useState(null);
    const [currentFiltradas, setCurrentFiltradas] = useState(null);
    const [currentBuffers, setCurrentBuffers] = useState(null);
    const [currentBufferIntersections, setCurrentBufferIntersections] = useState(null);

    // Para fertilización
    const [currentFertilizacion, setCurrentFertilizacion] = useState(null);

    // =========== CONTROL DEL MAPA ===========
    const [mapVisible, setMapVisible] = useState(false);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapStyle] = useState('mapbox://styles/mapbox/satellite-streets-v12');
    const mapRef = useRef(null);
    const [mapPosition, setMapPosition] = useState({ top: 200, left: 200 });
    const [loadingGeojson, setLoadingGeojson] = useState(false);

    // =========== CONTEO DE PALMAS ===========
    const [isConteoPalmas, setIsConteoPalmas] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageCoordinates, setImageCoordinates] = useState(null);

    // =========== CONTROL DE ELIMINACIONES ===========
    const [expandedSection, setExpandedSection] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [analysesToDelete, setAnalysesToDelete] = useState([]);
    const [deleting, setDeleting] = useState(false);

    // Estructura local con secciones (aplic. aéreas, fertilización, etc.)
    const [localAnalyses, setLocalAnalyses] = useState({});

    // Filtro por fecha del historial
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Mostrar/ocultar buffers y rutas no filtradas (Aplicaciones aéreas)
    const [showBuffers, setShowBuffers] = useState(true);
    const [showNoFiltradasRoutes, setShowNoFiltradasRoutes] = useState(true);

    // Mostrar/ocultar categorías de fertilización
    const [showFertPolygons, setShowFertPolygons] = useState(true);
    const [showFertRoutes, setShowFertRoutes] = useState(true);
    const [showFertIntersections, setShowFertIntersections] = useState(true);

    // ===========================================================
    // FUNCIÓN para “aplanar” y normalizar INTERSECCION
    // ===========================================================
    /**
     * Recorre todos los features:
     *  - Si geometry.type no es Point/LineString/Polygon => se descarta.
     *  - Convierte la propiedad INTERSECCION a "YES"/"NO" en una nueva prop `INTERSECCION_UNIFIED`.
     *  - Elimina la prop original INTERSECCION.
     */
    const preprocessGeojson = (geojsonData) => {
        if (!geojsonData || !Array.isArray(geojsonData.features)) return geojsonData;

        const validTypes = new Set(['Point', 'LineString', 'Polygon']);
        const newFeatures = [];

        for (const feature of geojsonData.features) {
            if (!feature.properties) feature.properties = {};

            // Normalizamos la geometry
            if (!feature.geometry || typeof feature.geometry !== 'object') {
                continue;
            }
            const gType = feature.geometry.type;
            if (!validTypes.has(gType)) {
                continue;
            }

            // Normalizar property "INTERSECCION" -> "INTERSECCION_UNIFIED"
            const val = feature.properties.INTERSECCION;
            let strVal = '';
            if (Array.isArray(val)) {
                const flattened = val.flat(Infinity);
                strVal = flattened.join(' ').toUpperCase();
            } else {
                strVal = String(val ?? '').toUpperCase();
            }
            feature.properties.INTERSECCION_UNIFIED = strVal.includes('YES') ? 'YES' : 'NO';

            // Eliminar la original
            delete feature.properties.INTERSECCION;

            newFeatures.push(feature);
        }

        return {
            ...geojsonData,
            features: newFeatures
        };
    };

    // ===========================================================
    // 1) CARGA INICIAL DE LOCALANALYSES
    // ===========================================================
    useEffect(() => {
        const safeAnalyses = (analyses && typeof analyses === 'object') ? { ...analyses } : {};

        const sections = ['Aplicaciones aéreas', 'Cosecha mecánica', 'Conteo de palmas', 'Fertilización'];

        // Aseguramos que cada sección sea array
        sections.forEach(section => {
            if (!Array.isArray(safeAnalyses[section])) {
                safeAnalyses[section] = [];
            }
        });

        // Filtrar .tiff en secciones (excepto apl. aéreas)
        sections.forEach(section => {
            if (section !== 'Aplicaciones aéreas') {
                safeAnalyses[section] = safeAnalyses[section].filter(file => !isTiff(file.file_name));
            }
        });

        // Agrupar filtradas/no_filtradas en "Aplicaciones aéreas"
        if (Array.isArray(safeAnalyses['Aplicaciones aéreas']) && safeAnalyses['Aplicaciones aéreas'].length > 0) {
            const grouped = {};
            safeAnalyses['Aplicaciones aéreas'].forEach(file => {
                if (!file?.file_name) return;
                if (isTiff(file.file_name)) return;
                const fname = file.file_name.split('/').pop() || '';

                let key;
                let isFiltradas = false;
                let isNoFiltradas = false;

                if (fname.includes('_no_filtradas')) {
                    key = fname.replace('_no_filtradas.geojson', '');
                    isNoFiltradas = true;
                } else if (fname.includes('_filtradas')) {
                    key = fname.replace('_filtradas.geojson', '');
                    isFiltradas = true;
                } else {
                    key = fname.replace('.geojson', '');
                }

                if (!grouped[key]) {
                    grouped[key] = {
                        key,
                        filtradas: null,
                        no_filtradas: null,
                        updated: file.updated || null,
                        reportes_pdf: Array.isArray(file.reportes_pdf) ? file.reportes_pdf : []
                    };
                }
                if (Array.isArray(file.reportes_pdf) && file.reportes_pdf.length > 0) {
                    grouped[key].reportes_pdf = file.reportes_pdf;
                }
                if (file.updated && (!grouped[key].updated || new Date(file.updated) > new Date(grouped[key].updated))) {
                    grouped[key].updated = file.updated;
                }
                if (isFiltradas) grouped[key].filtradas = file;
                if (isNoFiltradas) grouped[key].no_filtradas = file;
                if (!isFiltradas && !isNoFiltradas) {
                    grouped[key].no_filtradas = file;
                }
            });

            const combined = Object.values(grouped).map(item => ({
                key: item.key,
                updated: item.updated,
                filtradas: item.filtradas,
                no_filtradas: item.no_filtradas,
                reportes_pdf: item.reportes_pdf || []
            }));
            safeAnalyses['Aplicaciones aéreas'] = combined;
        }

        Object.keys(safeAnalyses).forEach(section => {
            safeAnalyses[section] = sortAnalysesByRecent(safeAnalyses[section]);
        });

        setLocalAnalyses(safeAnalyses);
    }, [analyses]);

    // ===========================================================
    // FUNCIONES AUXILIARES
    // ===========================================================
    function isTiff(fileName) {
        if (!fileName) return false;
        const ext = fileName.split('.').pop().toLowerCase();
        return ext === 'tif' || ext === 'tiff';
    }
    const validGeometryTypes = ['Point', 'LineString', 'Polygon'];

    // ===========================================================
    //     CAPAS MAPBOX (AÉREAS y otros)
    // ===========================================================
    // Puntos (filtradas)
    const pointLayer = {
        id: 'point-layer',
        type: 'circle',
        paint: {
            'circle-radius': 2,
            'circle-color': '#48ff00',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#48ff00'
        },
        filter: ['==', '$type', 'Point']
    };

    // Puntos (no filtradas)
    const pointLayerNoFiltradas = {
        id: 'point-layer-no-filtradas',
        type: 'circle',
        paint: {
            'circle-radius': 5,
            'circle-color': '#888888',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff'
        },
        filter: ['==', '$type', 'Point']
    };

    // Líneas (no filtradas)
    const lineLayerNoFiltradas = {
        id: 'line-layer-no-filtradas',
        type: 'line',
        paint: {
            'line-color': '#888888',
            'line-width': 2
        },
        layout: { 'line-sort-key': 0 },
        filter: ['==', '$type', 'LineString']
    };

    // Líneas (filtradas)
    const lineLayerFiltradas = {
        id: 'line-layer-filtradas',
        type: 'line',
        paint: {
            'line-color': '#ff0000',
            'line-width': 2
        },
        layout: { 'line-sort-key': 1 },
        filter: ['==', '$type', 'LineString']
    };

    // Polígonos (no filtradas)
    const polygonLayerNoFiltradas = {
        id: 'polygon-layer-no-filtradas',
        type: 'fill',
        paint: {
            'fill-color': '#48ff00',
            'fill-opacity': 0.4,
            'fill-outline-color': '#48ff00'
        },
        filter: ['==', '$type', 'Polygon']
    };

    // Polígonos (filtradas / apl. aéreas)
    const polygonLayerAplicacionesAereas = {
        id: 'polygon-layer-aplicaciones-aereas',
        type: 'fill',
        paint: {
            'fill-color': '#00FF00',
            'fill-opacity': 0.5,
            'fill-outline-color': '#00FF00'
        },
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', ['get', 'type'], 'overlap']]
    };

    // Polígonos overlap
    const polygonLayerOverlap = {
        id: 'polygon-layer-overlap',
        type: 'fill',
        paint: {
            'fill-color': '#FF0000',
            'fill-opacity': 0.7,
            'fill-outline-color': '#FF0000'
        },
        filter: ['all', ['==', '$type', 'Polygon'], ['==', ['get', 'type'], 'overlap']]
    };

    // Buffers
    const bufferPolygonLayer = {
        id: 'buffer-polygon-layer',
        type: 'fill',
        paint: {
            'fill-color': '#800080',
            'fill-opacity': 0.5,
            'fill-outline-color': '#800080'
        },
        filter: ['==', '$type', 'Polygon']
    };

    // Intersección de buffers
    const bufferIntersectionPolygonLayer = {
        id: 'buffer-intersection-polygon-layer',
        type: 'fill',
        paint: {
            'fill-color': '#0000FF',
            'fill-opacity': 0.7,
            'fill-outline-color': '#0000FF'
        },
        filter: ['==', '$type', 'Polygon']
    };

    // ====== CAPAS FERTILIZACION (usando INTERSECCION_UNIFIED) ======
    // Polígonos: si es intersección, rojo; de lo contrario, verde.
    const fertilizacionPolygonLayerInter = {
        id: 'fertilizacion-polygon-layer-inter',
        type: 'fill',
        paint: {
            'fill-color': '#FF0000',
            'fill-opacity': 0.4,
            'fill-outline-color': '#FF0000'
        },
        filter: [
            'all',
            ['==', '$type', 'Polygon'],
            ['==', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    const fertilizacionPolygonLayerNonInter = {
        id: 'fertilizacion-polygon-layer-non-inter',
        type: 'fill',
        paint: {
            'fill-color': '#00FF00',
            'fill-opacity': 0.4,
            'fill-outline-color': '#00FF00'
        },
        filter: [
            'all',
            ['==', '$type', 'Polygon'],
            ['!=', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    // Líneas: si es intersección, rojo; de lo contrario (ruta) morado.
    const fertilizacionLineLayerInter = {
        id: 'fertilizacion-line-layer-inter',
        type: 'line',
        paint: {
            'line-color': '#FF0000',
            'line-width': 2
        },
        filter: [
            'all',
            ['any',
                ['==', '$type', 'LineString'],
                ['==', '$type', 'MultiLineString']
            ],
            ['==', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    const fertilizacionLineLayerNonInter = {
        id: 'fertilizacion-line-layer-non-inter',
        type: 'line',
        paint: {
            'line-color': '#9b00a9', // color morado para la "Ruta"
            'line-width': 2
        },
        filter: [
            'all',
            ['any',
                ['==', '$type', 'LineString'],
                ['==', '$type', 'MultiLineString']
            ],
            ['!=', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    // Puntos
    const fertilizacionPointLayerInter = {
        id: 'fertilizacion-point-layer-inter',
        type: 'circle',
        paint: {
            'circle-radius': 6,
            'circle-color': '#FF0000',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#FF0000'
        },
        filter: [
            'all',
            ['==', '$type', 'Point'],
            ['==', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    const fertilizacionPointLayerNonInter = {
        id: 'fertilizacion-point-layer-non-inter',
        type: 'circle',
        paint: {
            'circle-radius': 6,
            'circle-color': '#00FF00',
            'circle-stroke-width': 1,
            'circle-stroke-color': '#00FF00'
        },
        filter: [
            'all',
            ['==', '$type', 'Point'],
            ['!=', 'INTERSECCION_UNIFIED', 'YES']
        ]
    };

    // ===========================================================
    // 2) AJUSTE DEL MAPA AL ENCENDER mapVisible
    // ===========================================================
    useEffect(() => {
        if (mapVisible && mapLoaded && mapRef.current) {
            const map = mapRef.current.getMap();
            map.resize();

            if (isConteoPalmas && imageUrl && imageCoordinates) {
                // Conteo palmas
                const lons = imageCoordinates.map(c => c[0]);
                const lats = imageCoordinates.map(c => c[1]);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                if ([minLon, maxLon, minLat, maxLat].every(v => typeof v === 'number' && !isNaN(v))) {
                    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 20, maxZoom: 18, duration: 0 });
                } else {
                    map.setCenter([0, 0]);
                    map.setZoom(2);
                }
            } else {
                // Combinamos features de todas las capas que tengamos cargadas
                let featuresCombined = [];
                if (currentGeojson?.features) featuresCombined.push(...currentGeojson.features);
                if (currentNoFiltradas?.features) featuresCombined.push(...currentNoFiltradas.features);
                if (currentFiltradas?.features) featuresCombined.push(...currentFiltradas.features);
                if (currentBuffers?.features) featuresCombined.push(...currentBuffers.features);
                if (currentBufferIntersections?.features) featuresCombined.push(...currentBufferIntersections.features);
                if (currentFertilizacion?.features) featuresCombined.push(...currentFertilizacion.features);

                featuresCombined = featuresCombined.filter(feature =>
                    feature.geometry && validGeometryTypes.includes(feature.geometry.type)
                );

                if (featuresCombined.length > 0) {
                    const combinedFc = { type: 'FeatureCollection', features: featuresCombined };
                    const bbox = turf.bbox(combinedFc);
                    if (bbox && bbox.length === 4 && bbox.every(v => typeof v === 'number' && !isNaN(v))) {
                        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 20, maxZoom: 18, duration: 0 });
                    } else {
                        map.setCenter([0, 0]);
                        map.setZoom(2);
                    }
                } else {
                    map.setCenter([0, 0]);
                    map.setZoom(2);
                }
            }
        }
    }, [
        mapVisible, mapLoaded,
        currentGeojson, currentNoFiltradas, currentFiltradas,
        currentBuffers, currentBufferIntersections, currentFertilizacion,
        isConteoPalmas, imageUrl, imageCoordinates
    ]);

    // ===========================================================
    // 3) RENDER: SI ESTÁ CARGANDO O SIN ANALYSIS
    // ===========================================================
    if (loading) {
        return (
            <ContentContainer>
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={2}>
                    <CircularProgress />
                    <Typography>Cargando análisis...</Typography>
                </Box>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            backgroundColor: 'var(--primary-color)',
                            '&:hover': {
                                backgroundColor: 'var(--primary-color-dark)',
                            },
                        }}
                    >
                        Cerrar
                    </Button>
                </Box>
            </ContentContainer>
        );
    }

    if (!localAnalyses || Object.keys(localAnalyses).length === 0) {
        return (
            <ContentContainer>
                <Typography>No hay análisis disponibles.</Typography>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        sx={{
                            backgroundColor: 'var(--primary-color)',
                            '&:hover': {
                                backgroundColor: 'var(--primary-color-dark)',
                            },
                        }}
                    >
                        Cerrar
                    </Button>
                </Box>
            </ContentContainer>
        );
    }

    // ===========================================================
    // 4) OTRAS FUNCIONES (formatDate, handleDownload, handleCheckbox)
    // ===========================================================
    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha desconocida';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Fecha desconocida';
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const mi = String(date.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    };


    function getReportDateCandidates(archivo) {
        const reportes = [];
        if (Array.isArray(archivo?.reportes_pdf)) reportes.push(...archivo.reportes_pdf);
        if (Array.isArray(archivo?.filtradas?.reportes_pdf)) reportes.push(...archivo.filtradas.reportes_pdf);
        if (Array.isArray(archivo?.no_filtradas?.reportes_pdf)) reportes.push(...archivo.no_filtradas.reportes_pdf);

        return reportes.flatMap(report => [
            report?.updated,
            report?.created_at,
            report?.createdAt,
            report?.FECHA_GENERACION,
            report?.CREATED_AT,
            report?.UPDATED_AT
        ]);
    }

    function getAnalysisTimestamp(archivo) {
        const candidates = [
            archivo?.updated,
            archivo?.created_at,
            archivo?.createdAt,
            archivo?.fecha,
            archivo?.FECHA,
            archivo?.FECHA_CREACION,
            archivo?.FECHA_GENERACION,
            archivo?.filtradas?.updated,
            archivo?.filtradas?.created_at,
            archivo?.no_filtradas?.updated,
            archivo?.no_filtradas?.created_at,
            ...getReportDateCandidates(archivo)
        ];

        return candidates.reduce((latest, value) => {
            if (!value) return latest;
            const timestamp = new Date(value).getTime();
            if (Number.isNaN(timestamp)) return latest;
            return Math.max(latest, timestamp);
        }, 0);
    }

    function sortAnalysesByRecent(files = []) {
        if (!Array.isArray(files)) return [];
        return [...files].sort((a, b) => getAnalysisTimestamp(b) - getAnalysisTimestamp(a));
    }

    const hasActiveDateFilter = Boolean(filterStartDate || filterEndDate);

    function getDateLimitTimestamp(dateValue, endOfDay = false) {
        if (!dateValue) return null;
        const date = new Date(`${dateValue}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
        const timestamp = date.getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    }

    function isWithinDateFilter(archivo) {
        if (!hasActiveDateFilter) return true;
        const timestamp = getAnalysisTimestamp(archivo);
        if (!timestamp) return false;

        const fromTimestamp = getDateLimitTimestamp(filterStartDate);
        const toTimestamp = getDateLimitTimestamp(filterEndDate, true);

        if (fromTimestamp !== null && timestamp < fromTimestamp) return false;
        if (toTimestamp !== null && timestamp > toTimestamp) return false;
        return true;
    }

    function getVisibleAnalyses() {
        const visible = {};
        Object.keys(localAnalyses || {}).forEach(section => {
            const files = Array.isArray(localAnalyses[section]) ? localAnalyses[section] : [];
            visible[section] = sortAnalysesByRecent(files.filter(isWithinDateFilter));
        });
        return visible;
    }

    function getTotalAnalysesCount(analysesObject = {}) {
        return Object.values(analysesObject).reduce((total, files) => {
            return total + (Array.isArray(files) ? files.length : 0);
        }, 0);
    }

    function clearDateFilters() {
        setFilterStartDate('');
        setFilterEndDate('');
    }


    const normalizeDownloadUrl = (value) => {
        if (!value) return null;
        const text = String(value).trim();
        if (!text) return null;
        if (/^https?:\/\//i.test(text)) return text;
        const base = String(API_BASE_URL || '').replace(/\/+$/, '');
        const path = text.startsWith('/') ? text : `/${text}`;
        return `${base}${path}`;
    };

    const handleDownload = (signedUrl, fileName) => {
        if (!signedUrl || !fileName) return;
        const normalizedUrl = normalizeDownloadUrl(signedUrl);
        if (!normalizedUrl) return;
        const link = document.createElement('a');
        link.href = normalizedUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getLatestPdfReport = (archivo) => {
        const reportes = [];

        if (Array.isArray(archivo?.reportes_pdf)) reportes.push(...archivo.reportes_pdf);
        if (Array.isArray(archivo?.filtradas?.reportes_pdf)) reportes.push(...archivo.filtradas.reportes_pdf);
        if (Array.isArray(archivo?.no_filtradas?.reportes_pdf)) reportes.push(...archivo.no_filtradas.reportes_pdf);

        const unique = new Map();
        reportes.forEach((report) => {
            const url = normalizeDownloadUrl(report?.signed_url || report?.download_url);
            if (!url) return;
            const key = report.gcs_path || report.file_name || report.download_url || report.signed_url;
            unique.set(key, { ...report, signed_url: url, download_url: normalizeDownloadUrl(report.download_url || report.signed_url) });
        });

        return Array.from(unique.values())
            .sort((a, b) => new Date(b.updated || 0) - new Date(a.updated || 0))[0] || null;
    };

    const getPdfFileName = (report) => {
        if (!report) return 'reporte.pdf';
        return report.file_name ? report.file_name.split('/').pop() : 'reporte.pdf';
    };

    const renderPdfReportButton = (archivo) => {
        const pdfReport = getLatestPdfReport(archivo);
        const hasPdf = !!pdfReport;

        return (
            <Tooltip
                title={
                    hasPdf
                        ? 'Descargar PDF del análisis'
                        : 'Aún no se ha generado el PDF de este análisis. Presiona el botón PDF para guardarlo aquí.'
                }
                arrow
            >
                <span>
                    <DownloadButton
                        onClick={() => hasPdf && handleDownload(pdfReport.signed_url || pdfReport.download_url, getPdfFileName(pdfReport))}
                        disabled={!hasPdf}
                        size="small"
                        sx={{
                            marginRight: '4px',
                            opacity: hasPdf ? 1 : 0.45,
                            cursor: hasPdf ? 'pointer' : 'default'
                        }}
                    >
                        <PictureAsPdfIcon fontSize="small" />
                    </DownloadButton>
                </span>
            </Tooltip>
        );
    };

    const handleCheckbox = (file) => {
        if (file) onCheckboxChange(file);
    };

    // ===========================================================
    // 5) toggleMapPreview: CARGA Y PREPROCESA EL GEOJSON
    // ===========================================================
    const toggleMapPreview = async (archivo, section, event) => {
        if (!archivo) return;

        const isPalmCount = (section === 'Conteo de palmas');
        const isAplicAereas = (section === 'Aplicaciones aéreas');
        const isFertilizacion = (section === 'Fertilización');

        // Decidimos un "nombre" interno para el archivo actual
        let currentFileName = 'unknown';
        if (isPalmCount) {
            currentFileName = archivo.file_name || archivo.signed_url || 'unknown_palmas';
        } else if (isAplicAereas) {
            currentFileName = archivo.key || 'unknown_aplicaciones';
        } else {
            currentFileName = archivo.file_name || archivo.signed_url || 'unknown';
        }

        // Ver si ya está visible (para togglear ON/OFF)
        const isCurrent = mapVisible && (
            (isConteoPalmas && imageUrl && imageUrl._fileName === currentFileName) ||
            (currentGeojson && currentGeojson._fileName === currentFileName) ||
            (currentNoFiltradas && currentNoFiltradas._fileName === currentFileName) ||
            (currentFiltradas && currentFiltradas._fileName === currentFileName) ||
            (currentBuffers && currentBuffers._fileName === currentFileName) ||
            (currentBufferIntersections && currentBufferIntersections._fileName === currentFileName) ||
            (currentFertilizacion && currentFertilizacion._fileName === currentFileName)
        );
        if (isCurrent) {
            // Apagamos todo
            setMapVisible(false);
            setCurrentGeojson(null);
            setCurrentNoFiltradas(null);
            setCurrentFiltradas(null);
            setCurrentBuffers(null);
            setCurrentBufferIntersections(null);
            setCurrentFertilizacion(null);
            setImageUrl(null);
            setIsConteoPalmas(false);
            setImageCoordinates(null);
            setMapLoaded(false);
            return;
        }

        // Posición del contenedor del mapa
        const rect = event.currentTarget.getBoundingClientRect();
        const previewLeft = rect.right + 10;
        const previewTop = rect.top + window.scrollY;
        setMapPosition({ top: previewTop, left: previewLeft });

        setLoadingGeojson(true);
        setMapVisible(true);
        setMapLoaded(false);

        // Limpieza previa
        setCurrentGeojson(null);
        setCurrentFiltradas(null);
        setCurrentNoFiltradas(null);
        setCurrentBuffers(null);
        setCurrentBufferIntersections(null);
        setCurrentFertilizacion(null);
        setImageUrl(null);
        setIsConteoPalmas(false);
        setImageCoordinates(null);

        try {
            // ----------------------------------------
            // CASO CONTEO PALMAS
            // ----------------------------------------
            if (isPalmCount) {
                const imageUrlValue = archivo.signed_url;
                setIsConteoPalmas(true);
                setImageUrl({ url: imageUrlValue, _fileName: currentFileName });

                const NWLat = parseFloat(archivo.bounds?.north_west_lat);
                const NWLon = parseFloat(archivo.bounds?.north_west_lon);
                const SELat = parseFloat(archivo.bounds?.south_east_lat);
                const SELon = parseFloat(archivo.bounds?.south_east_lon);

                if (!isNaN(NWLat) && !isNaN(NWLon) && !isNaN(SELat) && !isNaN(SELon)) {
                    setImageCoordinates([
                        [NWLon, NWLat],
                        [NWLon, SELat],
                        [SELon, SELat],
                        [SELon, NWLat]
                    ]);
                }

                // ----------------------------------------
                // CASO: APLICACIONES AÉREAS
                // ----------------------------------------
            } else if (isAplicAereas) {
                // Filtradas
                if (archivo.filtradas && archivo.filtradas.signed_url) {
                    const respFiltradas = await axios.get(archivo.filtradas.signed_url);
                    let fg = respFiltradas.data;

                    // Asegurar que sea FeatureCollection
                    if (fg.type === 'Feature') {
                        fg = { type: 'FeatureCollection', features: [fg] };
                    } else if (fg.type !== 'FeatureCollection') {
                        fg = { type: 'FeatureCollection', features: [] };
                    }
                    fg._fileName = currentFileName;

                    // Preprocesar
                    fg = preprocessGeojson(fg);
                    // Filtrar geometrías
                    fg.features = fg.features.filter(f => f.geometry && validGeometryTypes.includes(f.geometry.type));

                    const bufferFeatures = fg.features.filter(f => f.properties?.type === 'buffer');
                    const bufferIntersections = fg.features.filter(f => f.properties?.type === 'bufferIntersection');
                    const overlapFeatures = fg.features.filter(f => f.properties?.type === 'overlap');
                    const otherFeatures = fg.features.filter(f => !['buffer', 'bufferIntersection', 'overlap'].includes(f.properties?.type));

                    setCurrentFiltradas({
                        type: 'FeatureCollection',
                        features: otherFeatures,
                        _fileName: currentFileName
                    });

                    if (bufferFeatures.length > 0) {
                        setCurrentBuffers({ type: 'FeatureCollection', features: bufferFeatures, _fileName: currentFileName });
                    }
                    if (bufferIntersections.length > 0) {
                        setCurrentBufferIntersections({ type: 'FeatureCollection', features: bufferIntersections, _fileName: currentFileName });
                    }
                    if (overlapFeatures.length > 0) {
                        setCurrentBufferIntersections(prev => prev ? {
                            ...prev,
                            features: [...prev.features, ...overlapFeatures]
                        } : {
                            type: 'FeatureCollection', features: overlapFeatures, _fileName: currentFileName
                        });
                    }
                }

                // No Filtradas
                if (archivo.no_filtradas && archivo.no_filtradas.signed_url) {
                    const respNoFiltradas = await axios.get(archivo.no_filtradas.signed_url);
                    let ng = respNoFiltradas.data;

                    if (ng.type === 'Feature') {
                        ng = { type: 'FeatureCollection', features: [ng] };
                    } else if (ng.type !== 'FeatureCollection') {
                        ng = { type: 'FeatureCollection', features: [] };
                    }
                    ng._fileName = currentFileName;

                    // Preprocesar
                    ng = preprocessGeojson(ng);
                    ng.features = ng.features.filter(f => f.geometry && validGeometryTypes.includes(f.geometry.type));

                    const bufferFeatures = ng.features.filter(f => f.properties?.type === 'buffer');
                    const bufferIntersections = ng.features.filter(f => f.properties?.type === 'bufferIntersection');
                    const overlapFeatures = ng.features.filter(f => f.properties?.type === 'overlap');
                    const otherFeatures = ng.features.filter(f => !['buffer', 'bufferIntersection', 'overlap'].includes(f.properties?.type));

                    setCurrentNoFiltradas({
                        type: 'FeatureCollection',
                        features: otherFeatures,
                        _fileName: currentFileName
                    });

                    if (bufferFeatures.length > 0) {
                        setCurrentBuffers(prev => prev ? {
                            ...prev,
                            features: [...prev.features, ...bufferFeatures]
                        } : {
                            type: 'FeatureCollection', features: bufferFeatures, _fileName: currentFileName
                        });
                    }
                    if (bufferIntersections.length > 0) {
                        setCurrentBufferIntersections(prev => prev ? {
                            ...prev,
                            features: [...prev.features, ...bufferIntersections]
                        } : {
                            type: 'FeatureCollection', features: bufferIntersections, _fileName: currentFileName
                        });
                    }
                    if (overlapFeatures.length > 0) {
                        setCurrentBufferIntersections(prev => prev ? {
                            ...prev,
                            features: [...prev.features, ...overlapFeatures]
                        } : {
                            type: 'FeatureCollection', features: overlapFeatures, _fileName: currentFileName
                        });
                    }
                }

                if (!archivo.filtradas && !archivo.no_filtradas) {
                    setCurrentNoFiltradas({ type: 'FeatureCollection', features: [], _fileName: 'empty' });
                }

                // ----------------------------------------
                // CASO: FERTILIZACIÓN
                // ----------------------------------------
            } else if (isFertilizacion) {
                if (archivo.signed_url) {
                    const resp = await axios.get(archivo.signed_url);
                    let data = resp.data;

                    if (data.type === 'Feature') {
                        data = { type: 'FeatureCollection', features: [data] };
                    } else if (data.type !== 'FeatureCollection') {
                        data = { type: 'FeatureCollection', features: [] };
                    }
                    data._fileName = currentFileName;

                    // Preprocesamos
                    data = preprocessGeojson(data);
                    setCurrentFertilizacion(data);
                } else {
                    setCurrentFertilizacion({ type: 'FeatureCollection', features: [], _fileName: 'empty' });
                }

                // ----------------------------------------
                // CASO: OTRAS SECCIONES (Cosecha mecánica, etc.)
                // ----------------------------------------
            } else {
                if (archivo.signed_url) {
                    const resp = await axios.get(archivo.signed_url);
                    let data = resp.data;

                    if (data.type === 'Feature') {
                        data = { type: 'FeatureCollection', features: [data] };
                    } else if (data.type !== 'FeatureCollection') {
                        data = { type: 'FeatureCollection', features: [] };
                    }
                    data._fileName = currentFileName;

                    // Preprocesar
                    data = preprocessGeojson(data);
                    data.features = data.features.filter(f => f.geometry && validGeometryTypes.includes(f.geometry.type));

                    const bufferFeatures = data.features.filter(f => f.properties?.type === 'buffer');
                    const bufferIntersections = data.features.filter(f => f.properties?.type === 'bufferIntersection');
                    const overlapFeatures = data.features.filter(f => f.properties?.type === 'overlap');
                    const otherFeatures = data.features.filter(f => !['buffer', 'bufferIntersection', 'overlap'].includes(f.properties?.type));

                    if (otherFeatures.length > 0) {
                        setCurrentGeojson({
                            type: 'FeatureCollection',
                            features: otherFeatures,
                            _fileName: currentFileName
                        });
                    }
                    if (bufferFeatures.length > 0) {
                        setCurrentBuffers({ type: 'FeatureCollection', features: bufferFeatures, _fileName: currentFileName });
                    }
                    if (bufferIntersections.length > 0) {
                        setCurrentBufferIntersections({ type: 'FeatureCollection', features: bufferIntersections, _fileName: currentFileName });
                    }
                    if (overlapFeatures.length > 0) {
                        setCurrentBufferIntersections(prev => prev ? {
                            ...prev,
                            features: [...prev.features, ...overlapFeatures]
                        } : {
                            type: 'FeatureCollection', features: overlapFeatures, _fileName: currentFileName
                        });
                    }

                    if (
                        otherFeatures.length === 0 &&
                        bufferFeatures.length === 0 &&
                        bufferIntersections.length === 0 &&
                        overlapFeatures.length === 0
                    ) {
                        setCurrentGeojson({ type: 'FeatureCollection', features: [], _fileName: 'empty' });
                    }
                } else {
                    setCurrentGeojson({ type: 'FeatureCollection', features: [], _fileName: 'empty' });
                }
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);

            // Limpieza
            setIsConteoPalmas(false);
            setImageUrl(null);
            setCurrentGeojson({ type: 'FeatureCollection', features: [], _fileName: 'empty' });
            setCurrentNoFiltradas(null);
            setCurrentFiltradas(null);
            setCurrentBuffers(null);
            setCurrentBufferIntersections(null);
            setCurrentFertilizacion(null);
            setImageCoordinates(null);
        } finally {
            setLoadingGeojson(false);
        }
    };

    // ===========================================================
    // 6) ELIMINAR ARCHIVOS SELECCIONADOS
    // ===========================================================
    const handleDeleteClick = (section) => {
        const files = getSectionFiles(section);
        const filesToDelete = [];

        if (section === 'Aplicaciones aéreas') {
            files.forEach(item => {
                if (item.filtradas && selectedFiles.some(s => s.file_name === item.filtradas.file_name)) {
                    filesToDelete.push(item.filtradas);
                }
                if (item.no_filtradas && selectedFiles.some(s => s.file_name === item.no_filtradas.file_name)) {
                    filesToDelete.push(item.no_filtradas);
                }
            });
        } else {
            files.forEach(file => {
                if (selectedFiles.some(s => s.file_name === file.file_name)) {
                    filesToDelete.push(file);
                }
            });
        }

        if (filesToDelete.length > 0) {
            setAnalysesToDelete(filesToDelete);
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!analysesToDelete || analysesToDelete.length === 0) return;
        setDeleting(true);

        try {
            const deletePromises = analysesToDelete.map(file => {
                const id = file.analisis_id || file._id || (file.key ? file.key.split('_')[0] : null);
                if (!id) {
                    throw new Error(`ID de análisis no encontrado para el archivo: ${file.file_name}`);
                }
                return axios.delete(`${API_BASE_PYTHON_SERVICE}mapping/analisis/${id}`);
            });

            const responses = await Promise.all(deletePromises);
            const success = responses.every(resp => resp.data.status === 'success');

            if (success) {
                setLocalAnalyses(prev => {
                    const updated = { ...prev };
                    analysesToDelete.forEach(file => {
                        const section = Object.keys(updated).find(sec => {
                            if (sec === 'Aplicaciones aéreas') {
                                return updated[sec].some(item =>
                                    (item.filtradas && item.filtradas.file_name === file.file_name) ||
                                    (item.no_filtradas && item.no_filtradas.file_name === file.file_name)
                                );
                            } else {
                                return updated[sec].some(f => f.file_name === file.file_name);
                            }
                        });
                        if (section) {
                            if (section === 'Aplicaciones aéreas') {
                                updated[section] = updated[section].filter(item => {
                                    if (item.filtradas && item.filtradas.file_name === file.file_name) return false;
                                    if (item.no_filtradas && item.no_filtradas.file_name === file.file_name) return false;
                                    return true;
                                });
                            } else {
                                updated[section] = updated[section].filter(f => f.file_name !== file.file_name);
                            }
                        }
                    });
                    return updated;
                });

                toast.success('Análisis eliminados exitosamente.', {
                    position: toast.POSITION.TOP_RIGHT, autoClose: 5000
                });
            } else {
                toast.error('Error al eliminar algunos análisis.', {
                    position: toast.POSITION.TOP_RIGHT, autoClose: 5000
                });
            }
        } catch (error) {
            console.error('Error al eliminar los análisis:', error);
            toast.error('Ocurrió un error al eliminar los análisis.', {
                position: toast.POSITION.TOP_RIGHT, autoClose: 5000
            });
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setAnalysesToDelete([]);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAnalysesToDelete([]);
    };

    // ===========================================================
    // 7) CHECKBOX "SELECT ALL" POR SECCIÓN
    // ===========================================================
    const isAllSelected = (section) => {
        const files = getSectionFiles(section);
        if (!files.length) return false;
        if (section === 'Aplicaciones aéreas') {
            return files.every(item => {
                const filSel = item.filtradas ? selectedFiles.some(s => s.file_name === item.filtradas.file_name) : true;
                const noFilSel = item.no_filtradas ? selectedFiles.some(s => s.file_name === item.no_filtradas.file_name) : true;
                return filSel && noFilSel;
            });
        } else {
            return files.every(file => selectedFiles.some(s => s.file_name === file.file_name));
        }
    };

    const handleSelectAllChange = (section) => (event) => {
        const checked = event.target.checked;
        const files = getSectionFiles(section);

        if (checked) {
            if (section === 'Aplicaciones aéreas') {
                files.forEach(item => {
                    if (item.filtradas && !selectedFiles.some(s => s.file_name === item.filtradas.file_name)) {
                        onCheckboxChange(item.filtradas);
                    }
                    if (item.no_filtradas && !selectedFiles.some(s => s.file_name === item.no_filtradas.file_name)) {
                        onCheckboxChange(item.no_filtradas);
                    }
                });
            } else {
                files.forEach(file => {
                    if (!selectedFiles.some(s => s.file_name === file.file_name)) {
                        onCheckboxChange(file);
                    }
                });
            }
        } else {
            // Quitar
            if (section === 'Aplicaciones aéreas') {
                files.forEach(item => {
                    if (item.filtradas) onCheckboxChange(item.filtradas);
                    if (item.no_filtradas) onCheckboxChange(item.no_filtradas);
                });
            } else {
                files.forEach(file => {
                    onCheckboxChange(file);
                });
            }
        }
    };

    // ===========================================================
    // 8) RENDER FINAL DEL COMPONENTE
    // ===========================================================
    const visibleAnalyses = getVisibleAnalyses();
    const sectionKeys = Object.keys(visibleAnalyses);
    const totalAnalysesCount = getTotalAnalysesCount(localAnalyses);
    const visibleAnalysesCount = getTotalAnalysesCount(visibleAnalyses);
    const noDateFilterMatches = hasActiveDateFilter && totalAnalysesCount > 0 && visibleAnalysesCount === 0;

    const getSectionFiles = (section) => Array.isArray(visibleAnalyses[section]) ? visibleAnalyses[section] : [];

    return (
        <ContentContainer>
            <Box flexGrow={1} overflow="hidden">
                <Box
                    sx={{
                        mb: 1.5,
                        p: 1.25,
                        border: '1px solid #e6e8ef',
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                            display: 'block',
                            mb: 0.75,
                            color: '#1c2f49',
                            lineHeight: 1.2,
                        }}
                    >
                        Filtrar historial por fecha
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                            gap: 0.75,
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            value={filterStartDate}
                            onChange={(event) => setFilterStartDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: filterEndDate || undefined }}
                            sx={{
                                minWidth: 0,
                                '& .MuiInputBase-root': {
                                    height: 34,
                                    borderRadius: '10px',
                                    fontSize: '0.78rem',
                                },
                                '& .MuiInputBase-input': {
                                    px: 1,
                                    py: 0.6,
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.72rem',
                                },
                            }}
                        />
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            value={filterEndDate}
                            onChange={(event) => setFilterEndDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: filterStartDate || undefined }}
                            sx={{
                                minWidth: 0,
                                '& .MuiInputBase-root': {
                                    height: 34,
                                    borderRadius: '10px',
                                    fontSize: '0.78rem',
                                },
                                '& .MuiInputBase-input': {
                                    px: 1,
                                    py: 0.6,
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.72rem',
                                },
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={clearDateFilters}
                            disabled={!hasActiveDateFilter}
                            sx={{
                                minWidth: 58,
                                height: 34,
                                px: 1,
                                borderRadius: '10px',
                                fontSize: '0.72rem',
                                textTransform: 'none',
                                borderColor: 'var(--primary-color)',
                                color: 'var(--primary-color)',
                            }}
                        >
                            Limpiar
                        </Button>
                    </Box>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{
                            display: 'block',
                            mt: 0.75,
                            fontSize: '0.72rem',
                            lineHeight: 1.25,
                        }}
                    >
                        Mostrando {visibleAnalysesCount} de {totalAnalysesCount}. Más reciente primero.
                    </Typography>
                    {noDateFilterMatches && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: 'block', mt: 0.35, fontSize: '0.72rem', lineHeight: 1.2 }}
                        >
                            No hay análisis en el rango seleccionado.
                        </Typography>
                    )}
                </Box>

                {sectionKeys.map((section, index) => {
                    const sectionFiles = getSectionFiles(section);
                    const sectionCount = sectionFiles.length;
                    const isExpanded = expandedSection === section;
                    const allSelected = isAllSelected(section);
                    const isIndeterminate = !allSelected && selectedFiles.some(file => {
                        if (section === 'Aplicaciones aéreas') {
                            return sectionFiles.some(item =>
                                (item.filtradas && file.file_name === item.filtradas.file_name) ||
                                (item.no_filtradas && file.file_name === item.no_filtradas.file_name)
                            );
                        } else {
                            return sectionFiles.some(item => file.file_name === item.file_name);
                        }
                    });

                    return (
                        <Accordion
                            key={index}
                            expanded={isExpanded}
                            onChange={() => setExpandedSection(isExpanded ? false : section)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-content-${index}`}
                                id={`panel-header-${index}`}
                            >
                                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                                    <Box display="flex" alignItems="center">
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={isIndeterminate}
                                            onChange={handleSelectAllChange(section)}
                                            inputProps={{ 'aria-label': `Seleccionar todos en ${section}` }}
                                            size="small"
                                        />
                                        <Typography variant="h6" fontWeight="bold" style={{ marginLeft: '8px' }}>
                                            {section}
                                        </Typography>
                                        {loading ? (
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', marginLeft: '8px' }}>
                                                <CircularProgress size={16} />
                                            </Box>
                                        ) : (
                                            <Chip
                                                label={sectionCount}
                                                size="small"
                                                sx={{
                                                    marginLeft: '8px',
                                                    backgroundColor: 'var(--primary-color)',
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Tooltip title="Eliminar seleccionados">
                                        <span>
                                            <IconButton
                                                onClick={() => handleDeleteClick(section)}
                                                color="secondary"
                                                disabled={
                                                    section === 'Aplicaciones aéreas'
                                                        ? !sectionFiles.some(item =>
                                                            (item.filtradas && selectedFiles.some(s => s.file_name === item.filtradas.file_name)) ||
                                                            (item.no_filtradas && selectedFiles.some(s => s.file_name === item.no_filtradas.file_name))
                                                        )
                                                        : !sectionFiles.some(file =>
                                                            selectedFiles.some(s => s.file_name === file.file_name)
                                                        )
                                                }
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {sectionCount === 0 ? (
                                    <Typography>No hay archivos en esta sección.</Typography>
                                ) : (
                                    <ScrollableSection>
                                        <List>
                                            {sectionFiles.map((archivo, fileIdx) => {
                                                const isAplicAereas = (section === 'Aplicaciones aéreas');
                                                const isPalmCount = (section === 'Conteo de palmas');
                                                const isPdfOnly = archivo?.isPdfOnly === true;
                                                const analysisTimestamp = getAnalysisTimestamp(archivo);

                                                let fileNameToCheck, updatedDate;
                                                if (isAplicAereas) {
                                                    fileNameToCheck = archivo.key || 'unknown_aplicaciones';
                                                    updatedDate = analysisTimestamp ? formatDate(analysisTimestamp) : formatDate(archivo.updated);
                                                } else {
                                                    fileNameToCheck = archivo.file_name || archivo.signed_url || 'unknown';
                                                    updatedDate = analysisTimestamp ? formatDate(analysisTimestamp) : formatDate(archivo.updated);
                                                }

                                                // Si es .tiff en su caso
                                                if (isAplicAereas) {
                                                    const hasTiffFil = archivo.filtradas && isTiff(archivo.filtradas.file_name);
                                                    const hasTiffNoFil = archivo.no_filtradas && isTiff(archivo.no_filtradas.file_name);
                                                    if (hasTiffFil || hasTiffNoFil) return null;
                                                } else {
                                                    if (isTiff(fileNameToCheck)) return null;
                                                }

                                                // Determina si está check
                                                const isChecked = (() => {
                                                    if (isPdfOnly) return false;
                                                    if (isAplicAereas) {
                                                        let chk = true;
                                                        if (archivo.filtradas) {
                                                            chk = chk && selectedFiles.some(s => s.file_name === archivo.filtradas.file_name);
                                                        }
                                                        if (archivo.no_filtradas) {
                                                            chk = chk && selectedFiles.some(s => s.file_name === archivo.no_filtradas.file_name);
                                                        }
                                                        return chk;
                                                    } else {
                                                        return selectedFiles.some(sel => sel.file_name === archivo.file_name);
                                                    }
                                                })();

                                                return (
                                                    <ListItem
                                                        key={fileIdx}
                                                        divider
                                                        style={{ cursor: 'default', paddingLeft: '8px', paddingRight: '8px' }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={isChecked}
                                                            disabled={isPdfOnly}
                                                            onChange={() => {
                                                                if (isPdfOnly) return;
                                                                if (isAplicAereas) {
                                                                    if (archivo.filtradas) handleCheckbox(archivo.filtradas);
                                                                    if (archivo.no_filtradas) handleCheckbox(archivo.no_filtradas);
                                                                } else {
                                                                    handleCheckbox(archivo);
                                                                }
                                                            }}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            size="small"
                                                        />
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {updatedDate}
                                                                </Typography>
                                                            }
                                                        />
                                                        <Box display="flex" alignItems="center">
                                                            {!isPdfOnly && (
                                                                <IconButton
                                                                    onClick={(e) => toggleMapPreview(archivo, section, e)}
                                                                    size="small"
                                                                    sx={{
                                                                        marginRight: '4px',
                                                                        color: 'var(--primary-color)',
                                                                        '&:hover': {
                                                                            color: 'var(--primary-color-dark)',
                                                                        },
                                                                    }}
                                                                >
                                                                    {mapVisible && (
                                                                        (isAplicAereas && currentGeojson?._fileName === archivo.key) ||
                                                                        (archivo.filtradas && currentFiltradas?._fileName === archivo.key) ||
                                                                        (archivo.no_filtradas && currentNoFiltradas?._fileName === archivo.key) ||
                                                                        (currentGeojson && currentGeojson._fileName === fileNameToCheck) ||
                                                                        (currentBuffers && currentBuffers._fileName === fileNameToCheck) ||
                                                                        (currentBufferIntersections && currentBufferIntersections._fileName === fileNameToCheck) ||
                                                                        (currentFertilizacion && currentFertilizacion._fileName === fileNameToCheck) ||
                                                                        (isPalmCount && imageUrl && imageUrl._fileName === fileNameToCheck)
                                                                    )
                                                                        ? <VisibilityOffIcon fontSize="small" />
                                                                        : <VisibilityIcon fontSize="small" />
                                                                    }
                                                                </IconButton>
                                                            )}

                                                            {renderPdfReportButton(archivo)}

                                                            {isAplicAereas ? (
                                                                <>
                                                                    {archivo.filtradas && archivo.filtradas.file_name && (
                                                                        <Tooltip title="Ruta de vuelo filtrada">
                                                                            <DownloadButton
                                                                                onClick={() => handleDownload(
                                                                                    archivo.filtradas.signed_url,
                                                                                    archivo.filtradas.file_name
                                                                                        ? archivo.filtradas.file_name.split('/').pop()
                                                                                        : 'filtradas.geojson'
                                                                                )}
                                                                                size="small"
                                                                                sx={{ marginRight: '4px' }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </DownloadButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {archivo.no_filtradas && archivo.no_filtradas.file_name && (
                                                                        <Tooltip title="Ruta de vuelo no filtrada">
                                                                            <DownloadButton
                                                                                onClick={() => handleDownload(
                                                                                    archivo.no_filtradas.signed_url,
                                                                                    archivo.no_filtradas.file_name
                                                                                        ? archivo.no_filtradas.file_name.split('/').pop()
                                                                                        : 'no_filtradas.geojson'
                                                                                )}
                                                                                size="small"
                                                                                sx={{ marginRight: '4px' }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </DownloadButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {archivo.file_name && !isTiff(archivo.file_name) && (
                                                                        <Tooltip title="Ruta">
                                                                            <DownloadButton
                                                                                onClick={() => handleDownload(
                                                                                    archivo.signed_url,
                                                                                    archivo.file_name
                                                                                        ? archivo.file_name.split('/').pop()
                                                                                        : 'archivo.geojson'
                                                                                )}
                                                                                size="small"
                                                                                sx={{ marginRight: '4px' }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </DownloadButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Box>
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </ScrollableSection>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        backgroundColor: 'var(--primary-color)',
                        '&:hover': {
                            backgroundColor: 'var(--primary-color-dark)',
                        },
                    }}
                >
                    Cerrar
                </Button>
            </Box>

            {/* MAP PREVIEW */}
            {mapVisible && (currentGeojson || imageUrl || currentNoFiltradas ||
                currentFiltradas || currentBuffers ||
                currentBufferIntersections || currentFertilizacion || loadingGeojson) && (
                <MapPreviewContainer top={mapPosition.top} left={mapPosition.left} visible={true}>
                    {/* Leyenda dinámica */}
                    <Box
                        position="absolute"
                        top={10}
                        left={10}
                        display="flex"
                        flexDirection="row"
                        zIndex={10}
                        bgcolor="rgba(255,255,255,0.8)"
                        borderRadius="4px"
                        padding="4px"
                    >
                        {currentFertilizacion ? (
                            /* Leyenda FERTILIZACION con toggles */
                            <>
                                <Tooltip title={showFertPolygons ? "Ocultar Polígonos" : "Mostrar Polígonos"}>
                                    <IconButton
                                        onClick={() => setShowFertPolygons(prev => !prev)}
                                        size="small"
                                        style={{ margin: '2px' }}
                                    >
                                        <span
                                            style={{
                                                display: 'block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: showFertPolygons ? '#00FF00' : '#cccccc',
                                                border: '1px solid #000',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={showFertRoutes ? "Ocultar Rutas" : "Mostrar Rutas"}>
                                    <IconButton
                                        onClick={() => setShowFertRoutes(prev => !prev)}
                                        size="small"
                                        style={{ margin: '2px' }}
                                    >
                                        <span
                                            style={{
                                                display: 'block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: showFertRoutes ? '#9b00a9' : '#cccccc',
                                                border: '1px solid #000',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={showFertIntersections ? "Ocultar Intersecciones" : "Mostrar Intersecciones"}>
                                    <IconButton
                                        onClick={() => setShowFertIntersections(prev => !prev)}
                                        size="small"
                                        style={{ margin: '2px' }}
                                    >
                                        <span
                                            style={{
                                                display: 'block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: showFertIntersections ? '#FF0000' : '#cccccc',
                                                border: '1px solid #000',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            /* Leyenda APLICACIONES AÉREAS (u otras) con toggles */
                            <>
                                <Tooltip title={showBuffers ? "Ocultar Buffers" : "Mostrar Buffers"}>
                                    <IconButton onClick={() => setShowBuffers(prev => !prev)} size="small" style={{ margin: '2px' }}>
                                        <span
                                            style={{
                                                display: 'block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: showBuffers ? '#800080' : '#cccccc',
                                                border: '1px solid #000',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={showNoFiltradasRoutes ? "Ocultar Rutas de Vuelo No Filtradas" : "Mostrar Rutas de Vuelo No Filtradas"}>
                                    <IconButton onClick={() => setShowNoFiltradasRoutes(prev => !prev)} size="small" style={{ margin: '2px' }}>
                                        <span
                                            style={{
                                                display: 'block',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: showNoFiltradasRoutes ? '#888888' : '#cccccc',
                                                border: '1px solid #000',
                                                borderRadius: '2px'
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>

                    {(loadingGeojson || (
                        (!currentGeojson && !imageUrl && !currentNoFiltradas &&
                            !currentFiltradas && !currentBuffers &&
                            !currentBufferIntersections && !currentFertilizacion)
                    )) ? (
                        <Box
                            width="100%"
                            height="100%"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            bgcolor="#fff"
                        >
                            <CircularProgress size={30} />
                        </Box>
                    ) : (
                        <ReactMap
                            key={(
                                currentGeojson?._fileName ||
                                imageUrl?._fileName ||
                                currentNoFiltradas?._fileName ||
                                currentFiltradas?._fileName ||
                                currentBuffers?._fileName ||
                                currentBufferIntersections?._fileName ||
                                currentFertilizacion?._fileName ||
                                'mapKey'
                            )}
                            initialViewState={{
                                longitude: 0,
                                latitude: 0,
                                zoom: 2
                            }}
                            mapStyle={mapStyle}
                            mapboxAccessToken="pk.eyJ1IjoiamRtYW4iLCJhIjoiY20xNGpkN2d0MHlsejJ4cTA1Z3lwdGNhMCJ9.O5UY8q02UPPoCCMrobQ0sA"
                            style={{ height: '100%', width: '100%' }}
                            ref={mapRef}
                            onLoad={() => setMapLoaded(true)}
                            interactive={true}
                        >
                            <NavigationControl position="top-right" />

                            {/* Imagen Conteo Palmas */}
                            {isConteoPalmas && imageUrl && imageCoordinates && (
                                <Source id="image-overlay" type="image" url={imageUrl.url} coordinates={imageCoordinates}>
                                    <Layer
                                        id="image-overlay-layer"
                                        type="raster"
                                        paint={{ 'raster-opacity': 1 }}
                                    />
                                </Source>
                            )}

                            {/* Aéreas NO FILTRADAS (si showNoFiltradasRoutes está activo) */}
                            {(!isConteoPalmas && currentNoFiltradas && showNoFiltradasRoutes) && (
                                <Source id="preview-geojson-no-filtradas" type="geojson" data={currentNoFiltradas}>
                                    <Layer
                                        {...polygonLayerNoFiltradas}
                                        beforeId="polygon-layer-aplicaciones-aereas"
                                    />
                                    <Layer
                                        {...lineLayerNoFiltradas}
                                        beforeId="line-layer-filtradas"
                                    />
                                    <Layer
                                        {...pointLayerNoFiltradas}
                                        beforeId="point-layer"
                                    />
                                </Source>
                            )}

                            {/* Aéreas FILTRADAS */}
                            {(!isConteoPalmas && currentFiltradas) && (
                                <Source id="preview-geojson-filtradas" type="geojson" data={currentFiltradas}>
                                    <Layer {...polygonLayerAplicacionesAereas} />
                                    <Layer {...lineLayerFiltradas} />
                                    <Layer {...pointLayer} />
                                </Source>
                            )}

                            {/* GeoJSON general (cosecha mecánica, etc.) */}
                            {(!isConteoPalmas && currentGeojson && currentGeojson.features.length > 0) && (
                                <Source id="preview-geojson" type="geojson" data={currentGeojson}>
                                    <Layer {...polygonLayerAplicacionesAereas} />
                                    <Layer {...polygonLayerOverlap} />
                                    <Layer {...lineLayerFiltradas} />
                                    <Layer {...pointLayer} />
                                </Source>
                            )}

                            {/* Buffers (solo si showBuffers=true) */}
                            {(!isConteoPalmas && currentBuffers && currentBuffers.features.length > 0 && showBuffers) && (
                                <Source id="preview-geojson-buffers" type="geojson" data={currentBuffers}>
                                    <Layer {...bufferPolygonLayer} />
                                </Source>
                            )}

                            {/* Buffer Intersections (solo si showBuffers=true) */}
                            {(!isConteoPalmas && currentBufferIntersections && currentBufferIntersections.features.length > 0 && showBuffers) && (
                                <Source id="preview-geojson-buffer-intersections" type="geojson" data={currentBufferIntersections}>
                                    <Layer {...bufferIntersectionPolygonLayer} />
                                </Source>
                            )}

                            {/* Fertilización (con INTERSECCION_UNIFIED) */}
                            {(!isConteoPalmas && currentFertilizacion && currentFertilizacion.features.length > 0) && (
                                <Source id="fertilizacion-geojson" type="geojson" data={currentFertilizacion}>
                                    {/* Polígonos NO Intersección */}
                                    {showFertPolygons && (
                                        <Layer {...fertilizacionPolygonLayerNonInter} />
                                    )}
                                    {/* Polígonos Intersección */}
                                    {showFertIntersections && (
                                        <Layer {...fertilizacionPolygonLayerInter} />
                                    )}
                                    {/* Líneas NO Intersección (ruta) */}
                                    {showFertRoutes && (
                                        <Layer {...fertilizacionLineLayerNonInter} />
                                    )}
                                    {/* Líneas Intersección */}
                                    {showFertIntersections && (
                                        <Layer {...fertilizacionLineLayerInter} />
                                    )}
                                    {/* Puntos NO Intersección */}
                                    {showFertPolygons && (
                                        <Layer {...fertilizacionPointLayerNonInter} />
                                    )}
                                    {/* Puntos Intersección */}
                                    {showFertIntersections && (
                                        <Layer {...fertilizacionPointLayerInter} />
                                    )}
                                </Source>
                            )}
                        </ReactMap>
                    )}
                </MapPreviewContainer>
            )}

            {/* Diálogo confirmación eliminar */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        ¿Estás seguro de que deseas eliminar {analysesToDelete.length > 1 ? 'los análisis seleccionados' : 'este análisis'}? Esta acción no se puede deshacer.
                    </DialogContentText>
                    {analysesToDelete.length > 0 && (
                        <List>
                            {analysesToDelete.map((file, idx) => (
                                <ListItem key={idx}>
                                    <ListItemText primary={file.file_name || 'Archivo desconocido'} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleting}
                        sx={{
                            color: 'var(--primary-color)',
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="secondary" disabled={deleting}>
                        {deleting ? <CircularProgress size={24} /> : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </ContentContainer>
    );
};

export default HistorialContent;
